import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  userId: number;
  role: string;
  sessionId?: string;
  isTempPassword?: boolean;
  [key: string]: any;
}

interface AuthUser {
  userId: number;
  sessionId?: string;
  isTempPassword?: boolean;
  [key: string]: any;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  permissions: { role?: string };
  signIn: (token: string) => Promise<void>;
  signOut: () => void;
  refreshToken: () => Promise<string>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [permissions, setPermissions] = useState<{ role?: string }>({});

  const signOut = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setPermissions({});
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }, []);

  const refreshToken = useCallback(async (): Promise<string> => {
    try {
      const currentRefreshToken = localStorage.getItem('refreshToken');
      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Refresh-Token': currentRefreshToken,
        },
      });

      if (response.ok) {
        const { accessToken } = await response.json();
        localStorage.setItem('token', accessToken);
        return accessToken;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      signOut();
      throw error;
    }
  }, [signOut]);

  const signIn = useCallback(async (token: string): Promise<void> => {
    try {
      if (typeof token !== 'string' || token.trim() === '') {
        throw new Error('Invalid token specified');
      }

      const decodedToken = jwtDecode<DecodedToken>(token);
      const { role, ...rest } = decodedToken;

      setIsAuthenticated(true);
      setUser(rest);
      setPermissions({ role });
      localStorage.setItem('token', token);
    } catch (error) {
      console.error('Sign in failed:', error);
      signOut();
      throw error;
    }
  }, [signOut]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    permissions,
    signIn,
    signOut,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
